import React, { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, DocumentDuplicateIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';

interface VariableTemplate {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  type: string;
  dataType: string;
  defaultValue?: string;
  isRequired: boolean;
  isSecret: boolean;
}

interface ResolvedVariable {
  template: VariableTemplate;
  value?: string;
  resolvedValue?: string; // Masked value for display
  actualValue?: string; // Actual unmasked value for submission
  isOverridden: boolean;
}

interface ClonePipelineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (variables: Array<{ templateId: string; value: string }>, excludedVariables?: string[]) => void;
  variables: ResolvedVariable[];
  engagementName: string;
  isLoading?: boolean;
  mode?: 'clone' | 'trigger'; // New prop to distinguish between clone and trigger
  pipelineName?: string; // For trigger mode
}

const ClonePipelineModal: React.FC<ClonePipelineModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  variables,
  engagementName,
  isLoading = false,
  mode = 'clone',
  pipelineName
}) => {
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [actualValues, setActualValues] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [excludedVariables, setExcludedVariables] = useState<Set<string>>(new Set());

  // Initialize form values when modal opens or variables change
  useEffect(() => {
    if (isOpen && variables.length > 0) {
      const initialDisplayValues: Record<string, string> = {};
      const initialActualValues: Record<string, string> = {};

      variables.forEach((variable) => {
        // For display: use masked resolvedValue
        const displayValue = variable.resolvedValue || variable.value || variable.template.defaultValue || '';
        initialDisplayValues[variable.template.id] = displayValue;

        // For submission: use actual unmasked value
        const actualValue = variable.actualValue || variable.value || variable.template.defaultValue || '';
        initialActualValues[variable.template.id] = actualValue;
      });

      setFormValues(initialDisplayValues);
      setActualValues(initialActualValues);
      setValidationErrors({});
      setExcludedVariables(new Set()); // Reset excluded variables
    }
  }, [isOpen, variables]);

  const handleInputChange = (templateId: string, value: string) => {
    setFormValues(prev => ({
      ...prev,
      [templateId]: value
    }));

    // IMPORTANT: Also update actualValues so the changed value gets submitted
    setActualValues(prev => ({
      ...prev,
      [templateId]: value
    }));

    // Clear validation error for this field
    if (validationErrors[templateId]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[templateId];
        return newErrors;
      });
    }
  };

  const handleExcludeToggle = (templateId: string) => {
    setExcludedVariables(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    variables.forEach((variable) => {
      // Skip validation for excluded variables
      if (excludedVariables.has(variable.template.id)) {
        return;
      }

      const value = formValues[variable.template.id] || '';

      // Check required fields
      if (variable.template.isRequired && !value.trim()) {
        errors[variable.template.id] = 'This field is required';
      }

      // Validate data types
      if (value.trim()) {
        if (variable.template.dataType === 'NUMBER') {
          if (isNaN(Number(value))) {
            errors[variable.template.id] = 'Must be a valid number';
          }
        } else if (variable.template.dataType === 'BOOLEAN') {
          if (value !== 'true' && value !== 'false') {
            errors[variable.template.id] = 'Must be true or false';
          }
        } else if (variable.template.dataType === 'JSON') {
          try {
            JSON.parse(value);
          } catch {
            errors[variable.template.id] = 'Must be valid JSON';
          }
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    // Prepare variables array with actual unmasked values for submission
    // Filter out excluded variables
    const updatedVariables = variables
      .filter(variable => !excludedVariables.has(variable.template.id))
      .map(variable => ({
        templateId: variable.template.id,
        value: actualValues[variable.template.id] || ''
      }));

    // Get list of excluded variable names for notification
    const excludedVariableNames = variables
      .filter(variable => excludedVariables.has(variable.template.id))
      .map(variable => variable.template.name);

    onConfirm(updatedVariables, excludedVariableNames);
  };

  const renderInput = (variable: ResolvedVariable) => {
    const { template } = variable;
    const value = formValues[template.id] || '';
    const hasError = !!validationErrors[template.id];

    const baseInputClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
      hasError ? 'border-red-500' : 'border-gray-300'
    }`;

    switch (template.dataType) {
      case 'BOOLEAN':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(template.id, e.target.value)}
            className={baseInputClasses}
            disabled={isLoading}
          >
            <option value="">Select...</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        );

      case 'JSON':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(template.id, e.target.value)}
            className={baseInputClasses}
            rows={4}
            placeholder={template.description || 'Enter JSON...'}
            disabled={isLoading}
          />
        );

      case 'NUMBER':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(template.id, e.target.value)}
            className={baseInputClasses}
            placeholder={template.description || 'Enter number...'}
            disabled={isLoading}
          />
        );

      case 'SECRET':
        return (
          <input
            type="text"
            value={value}
            readOnly
            className={`${baseInputClasses} font-mono text-sm bg-gray-50 cursor-not-allowed`}
            placeholder={template.description || 'Secret value (hidden)'}
            disabled={isLoading}
            title="Secret values are masked and read-only"
          />
        );

      default: // STRING
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(template.id, e.target.value)}
            className={baseInputClasses}
            placeholder={template.description || 'Enter value...'}
            disabled={isLoading}
          />
        );
    }
  };

  // Group variables by type
  const groupedVariables = variables.reduce((acc, variable) => {
    const type = variable.template.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(variable);
    return acc;
  }, {} as Record<string, ResolvedVariable[]>);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-6xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <DocumentDuplicateIcon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
                        {mode === 'trigger' ? 'Trigger Pipeline' : 'Clone Pipeline Configuration'}
                      </Dialog.Title>
                      <p className="mt-1 text-sm text-gray-500">
                        {mode === 'trigger'
                          ? `Review and update variables before triggering: ${pipelineName || engagementName}`
                          : `Review and update variable values for "${engagementName}"`
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                    disabled={isLoading}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Variables Table */}
                <div className="mt-4 max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  {Object.keys(groupedVariables).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No variables configured for this engagement
                    </div>
                  ) : (
                    Object.entries(groupedVariables).map(([type, vars]) => (
                      <div key={type} className="mb-6 last:mb-0">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-700 uppercase">
                            {type.replace('_', ' ')} Variables
                          </h4>
                        </div>
                        <table className="min-w-full divide-y divide-gray-200 table-fixed">
                          <colgroup>
                            <col style={{ width: '25%' }} />
                            <col style={{ width: '20%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '45%' }} />
                          </colgroup>
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Variable Name
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Exclude
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Value
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {vars.map((variable) => (
                              <tr key={variable.template.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  <div>
                                    {variable.template.description || variable.template.displayName}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <div className="flex items-center">
                                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-700">
                                      {variable.template.name}
                                    </code>
                                    {variable.template.isRequired && (
                                      <span className="text-red-500 ml-1">*</span>
                                    )}
                                    {variable.template.isSecret && (
                                      <span className="ml-2 text-xs text-gray-500">(secret)</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <input
                                    type="checkbox"
                                    checked={excludedVariables.has(variable.template.id)}
                                    onChange={() => handleExcludeToggle(variable.template.id)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    disabled={isLoading}
                                    title="Exclude this variable from being sent to Azure"
                                  />
                                </td>
                                <td className="px-4 py-3">
                                  <div>
                                    {renderInput(variable)}
                                    {validationErrors[variable.template.id] && (
                                      <div className="flex items-center mt-1 text-xs text-red-600">
                                        <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                                        {validationErrors[variable.template.id]}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                    disabled={isLoading || variables.length === 0}
                  >
                    {isLoading ? (
                      <>
                        <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {mode === 'trigger' ? 'Triggering...' : 'Cloning...'}
                      </>
                    ) : (
                      mode === 'trigger' ? 'Trigger Pipeline' : 'Clone Pipeline'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ClonePipelineModal;
